import Card from '@mui/material/Card';
import React from 'react';
import { BrollCaptionVideo, Status } from '../../../@types';
import { Styles } from '@t2c/shared-fe';
import Avatar from '@mui/material/Avatar';
import { StyledCard, AvatarBox, FooterBox } from '../../avatar-library/AvatarSection';
import {
  BrollRenderStatus,
  CardTypeEnum,
  extractDate,
  extractTime,
  MediaStatus,
} from '../../../utils';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { ConfirmDialog } from '../../../Common/dialog';
import { useBrollCaptionCardActions } from '../../../hooks';
import { BrollCloneDialog } from '../BrollCloneDialog';
import { DropDownOptions, ProgressStuckNote, StatusWithProgressBar } from '../../../Common';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { Iconify } from '@sdf/base';
import { RenameDialog } from '../../text-to-speech/RenameDialog';
import { DeleteConfirmModal } from '../../delete-confirm/DeleteConfirmModal';

interface Props {
  data: BrollCaptionVideo;
  refreshBrollData: () => void;
  handleOnPreview: (_: string) => void;
  handleEdit?: (id: string) => void;
}

export const BrollCaptionCard: React.FC<Props> = ({
  data,
  refreshBrollData,
  handleOnPreview,
  handleEdit,
}) => {
  const {
    menuAnchorEl,
    setMenuAnchorEl,
    isDeleteDialogOpen,
    closeDeleteDialog,
    confirmDeleteVideo,
    isDeletingVideo,
    menuActions,
    goToVideoEditor,
    isCloneDialogOpen,
    closeCloneDialog,
    isCloninBrollCaption,
    handleOnClone,
    videoDataStatus,
    overallPercentage,
    hideDropdownButton,
    closeRenameDialog,
    isEditNameLoading,
    handleOnRename,
    isRenameDialogOpen,
  } = useBrollCaptionCardActions({
    videoData: data,
    refreshBrollData,
    handleOnPreview,
    handleEdit,
  });

  const { id, name, thumbnail, updatedAt, renderedUrl, status, renderStatus } = data;
  const cardStatus =
    videoDataStatus === Status.Success && renderStatus === BrollRenderStatus.InDraft
      ? MediaStatus.NotStarted
      : (videoDataStatus as MediaStatus);
  return (
    <>
      <StyledCard
        variant="outlined"
        status={cardStatus}
        onClick={
          renderStatus === BrollRenderStatus.Success
            ? () => handleOnPreview(id)
            : (videoDataStatus === Status.Success || status === MediaStatus.Success) &&
              !hideDropdownButton
            ? handleEdit
              ? () => handleEdit(id)
              : goToVideoEditor
            : () => null
        }
      >
        <AvatarBox height="85%" status={cardStatus} className="avatar-box">
          <Avatar alt={name} src={thumbnail} sx={{ width: '152px', height: '152px' }}>
            <Box
              sx={{
                width: '152px',
                height: '152px',
                background: (theme) => theme.palette.background.primary,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Iconify
                icon={'ooui:user-avatar'}
                width={'24px'}
                height={'24px'}
                mr={0.5}
                color={(theme) => theme.palette.text.primary}
              />
            </Box>
          </Avatar>
          <Stack
            direction="row"
            gap={0.5}
            sx={{
              position: 'absolute',
              top: '10px',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            {videoDataStatus === Status.Error && (
              <Tooltip
                title={
                  'We hit a glitch in the matrix! Your video didn’t make it. No credit deducted. Give it another shot!'
                }
              >
                <Box
                  sx={{
                    background: (theme) => theme.palette.gradients.gradient8,
                    padding: '0 12px',
                    borderTopRightRadius: '20px',
                    borderBottomRightRadius: '20px',
                    fontSize: '14px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Iconify
                    icon={'heroicons-outline:exclamation'}
                    width={'20px'}
                    height={'20px'}
                    color={(theme) => theme.palette.text.primary}
                  />
                  <span>Failed </span>
                </Box>
              </Tooltip>
            )}
            {(videoDataStatus === Status.NotStarted ||
              (videoDataStatus === Status.Success &&
                renderStatus === BrollRenderStatus.InDraft)) && (
              <Box
                sx={{
                  background: (theme) => theme.palette.gradients.gradient9,
                  padding: '2px 12px',
                  borderTopRightRadius: '20px',
                  borderBottomRightRadius: '20px',
                  fontSize: '14px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <span>Draft </span>
              </Box>
            )}
            {((videoDataStatus !== Status.NotStarted &&
              videoDataStatus !== Status.Error &&
              videoDataStatus !== Status.Success) ||
              (videoDataStatus === Status.Success &&
                (renderStatus === BrollRenderStatus.InProgress ||
                  renderStatus === BrollRenderStatus.InQueue))) && (
              <Box
                sx={{
                  padding: '0 12px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <ProgressStuckNote showToolTip={true} />
              </Box>
            )}
            <div></div>
            {!hideDropdownButton && (
              <DropDownOptions
                menuOptions={menuActions}
                anchorEl={menuAnchorEl}
                setAnchorEl={setMenuAnchorEl}
              />
            )}
          </Stack>
          <Stack
            sx={{
              minWidth: 150,
              width: '40%',
              position: 'absolute',
              top: '65%',
            }}
          >
            <StatusWithProgressBar
              progressPercentage={overallPercentage}
              status={videoDataStatus}
              isBrollCaption
              showStatusOnSuccess={renderedUrl && !hideDropdownButton}
            />
          </Stack>
        </AvatarBox>
        <FooterBox status={cardStatus} className="avatar-footer">
          <Tooltip title={name}>
            <Typography
              sx={{
                display: 'inline-block',
                maxWidth: '50%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              className="avatar-name"
              fontWeight={400}
              fontSize={14}
            >
              {name ?? '--'}
            </Typography>
          </Tooltip>
          <Typography
            className="avatar-time"
            variant="caption"
            sx={{
              fontSize: 12,
            }}
          >
            {extractDate(updatedAt)}
            &nbsp;
            {extractTime(updatedAt)}
          </Typography>
        </FooterBox>
      </StyledCard>
      <DeleteConfirmModal
        handleCancel={closeDeleteDialog}
        open={isDeleteDialogOpen}
        id={id}
        name={name}
        refetchData={refreshBrollData}
        type={CardTypeEnum.Broll}
      />
      <BrollCloneDialog
        shouldOpenDialog={isCloneDialogOpen}
        handleCloseDialog={closeCloneDialog}
        handleOnClone={handleOnClone}
        isLoading={isCloninBrollCaption}
        thumbnail={thumbnail}
      />
      <RenameDialog
        shouldOpenDialog={isRenameDialogOpen}
        handleCloseDialog={closeRenameDialog}
        initialName={name || ''}
        handleOnRename={handleOnRename}
        isLoading={isEditNameLoading}
        dialogTitle="Rename"
      />
    </>
  );
};

const styles: Styles = {
  card: {
    width: 350,
  },
};
